// TODO fix class method not uses this, therefore it is rather util than class method
/* eslint-disable class-methods-use-this */
// TODO fix circular dependencies
/* eslint-disable import/no-cycle */
import {
  DateTime,
  DateTimeUnit,
  DurationUnit,
  DurationObjectUnits,
} from 'luxon';
import {
  ISODate,
  Language,
  Timezone,
} from '@/entities';
import { ValueObject } from './value-object.base';
import { TimezoneVo } from './timezone.vo';

export enum DayOfWeek {
  mon = 'mon',
  tue = 'tue',
  wed = 'wed',
  thu = 'thu',
  fri = 'fri',
  sat = 'sat',
  sun = 'sun',
}

const MAP_WEEK_DAY_NUMBER_TO_DAY_OF_WEEK: Record<number, DayOfWeek> = {
  1: DayOfWeek.mon,
  2: DayOfWeek.tue,
  3: DayOfWeek.wed,
  4: DayOfWeek.thu,
  5: DayOfWeek.fri,
  6: DayOfWeek.sat,
  7: DayOfWeek.sun,
};

export enum DateFormat {
  '1970-01-01' = '1970-01-01',
  '1:00 am' = '1:00 am',
  'Jan 1970' = 'Jan 1970',
  'Jan 1' = 'Jan 1',
  'Jan 1, 1970' = 'Jan 1, 1970',
  'Jan 1, 1:00 am' = 'Jan 1, 1:00 am',
  'Jan 1, 1970, 1:00 am' = 'Jan 1, 1970, 1:00 am',
  'Monday, Jan 1, 1970' = 'Monday, Jan 1 1970',
  '1:00 am, Jan 1, Monday' = '1:00 am, Jan 1, Monday',
}

const MAP_DATE_FORMAT_TO_LIB_DATE_FORMAT: Record<DateFormat, string> = {
  [DateFormat['1970-01-01']]: 'yyyy-LL-dd',
  [DateFormat['1:00 am']]: 'h:mm a',
  [DateFormat['Jan 1']]: 'LLL d',
  [DateFormat['Jan 1970']]: 'LLL yyyy',
  [DateFormat['Jan 1, 1970']]: 'LLL d, yyyy',
  [DateFormat['Jan 1, 1:00 am']]: 'LLL d, h:mm a',
  [DateFormat['Jan 1, 1970, 1:00 am']]: 'LLL d, yyyy, h:mm a',
  [DateFormat['Monday, Jan 1, 1970']]: 'cccc, LLL d, yyyy',
  [DateFormat['1:00 am, Jan 1, Monday']]: 'h:mm a, LLL d, cccc',
};

interface IDateVoProps {
  date: DateTime;
  timezone: TimezoneVo;
  language: Language;
}

interface IDateVoRawProps {
  date: ISODate;
  timezone: Timezone;
  language: Language;
}

export class DateVo extends ValueObject<IDateVoProps> {
  constructor({
    date,
    timezone,
    language,
  }: IDateVoRawProps) {
    if (!DateTime.fromISO(date).isValid) {
      throw new Error('Invalid date');
    }
    super({
      date: DateTime.fromISO(date).setZone(timezone).setLocale(language),
      timezone: new TimezoneVo(timezone),
      language,
    });
  }

  public get value() {
    return this.clone().props.date;
  }

  public get iso(): ISODate {
    return this.clone().props.date.toISO()!;
  }

  public static now({
    timezone,
    language,
  }: Omit<IDateVoRawProps, 'date'>) {
    return new DateVo({
      date: DateTime.now().setZone(timezone).setLocale(language).toISO()!,
      timezone,
      language,
    });
  }

  public getDayOfTheWeek() {
    return MAP_WEEK_DAY_NUMBER_TO_DAY_OF_WEEK[this.clone().props.date.weekday];
  }

  public getMilliseconds() {
    return this.clone().props.date.millisecond;
  }

  public getSeconds() {
    return this.clone().props.date.second;
  }

  public getMinutes() {
    return this.clone().props.date.minute;
  }

  public getHours() {
    return this.clone().props.date.hour;
  }

  public getDay() {
    return this.clone().props.date.day;
  }

  public getMonth() {
    return this.clone().props.date.month;
  }

  public getYear() {
    return this.clone().props.date.year;
  }

  public isSameDay(compareTo: DateVo) {
    return this.clone().props.date.hasSame(compareTo.clone().props.date, 'day');
  }

  public format(format: DateFormat) {
    return this.clone().props.date.toFormat(MAP_DATE_FORMAT_TO_LIB_DATE_FORMAT[format]);
  }

  public add(amount: DurationObjectUnits) {
    return new DateVo({
      date: this.clone().props.date.plus(amount).toISO()!,
      timezone: this.props.timezone.value,
      language: this.props.language,
    });
  }

  public subtract(amount: DurationObjectUnits) {
    return new DateVo({
      date: this.clone().props.date.minus(amount).toISO()!,
      timezone: this.props.timezone.value,
      language: this.props.language,
    });
  }

  public startOf(unit: DateTimeUnit) {
    return new DateVo({
      date: this.clone().props.date.startOf(unit).toISO()!,
      timezone: this.props.timezone.value,
      language: this.props.language,
    });
  }

  public endOf(unit: DateTimeUnit) {
    return new DateVo({
      date: this.clone().props.date.endOf(unit).toISO()!,
      timezone: this.props.timezone.value,
      language: this.props.language,
    });
  }

  public setTimezone(timezone: TimezoneVo) {
    return new DateVo({
      date: this.clone().props.date.setZone(timezone.value).toISO()!,
      timezone: timezone.value,
      language: this.props.language,
    });
  }

  public setLanguage(language: Language) {
    return new DateVo({
      date: this.clone().props.date.setLocale(language).toISO()!,
      timezone: this.props.timezone.value,
      language,
    });
  }

  public diff(compareTo: DateVo, unit: DurationUnit) {
    return this.clone().props.date.diff(compareTo.clone().props.date, unit).get(unit);
  }

  public gt(compareTo: DateVo) {
    return this.clone().props.date.toMillis() > compareTo.clone().props.date.toMillis();
  }

  public gte(compareTo: DateVo) {
    return this.clone().props.date.toMillis() >= compareTo.clone().props.date.toMillis();
  }

  public lt(compareTo: DateVo) {
    return this.clone().props.date.toMillis() < compareTo.clone().props.date.toMillis();
  }

  public lte(compareTo: DateVo) {
    return this.clone().props.date.toMillis() <= compareTo.clone().props.date.toMillis();
  }

  public equals(compareTo: DateVo) {
    return this.clone().props.date.equals(compareTo.clone().props.date);
  }

  public clone() {
    return new DateVo({
      date: this.props.date.toISO()!,
      timezone: this.props.timezone.value,
      language: this.props.language,
    });
  }

  protected validate({ date, timezone }: IDateVoProps) {
    if (!date.isValid) {
      throw new Error('Incorrect date');
    }
    if (!timezone) {
      throw new Error('Timezone cannot be empty');
    }
  }
}
